import { FC, useCallback, useMemo, useState } from 'react';
import { ListNavigationStateContext, ListNavigationState, ListType } from './ListNavigationStateContext';
import { ReactSimpleComponentProps } from '@/custom-types';

const STORAGE_KEY_PREFIX = 'listNavigationState_';

export const ListNavigationStateContextProvider: FC<ReactSimpleComponentProps> = ({ children }) => {
  const [states, setStates] = useState<Record<ListType, ListNavigationState | null>>(() => {
    const initialStates: Record<ListType, ListNavigationState | null> = {
      incidents: null,
      contracts: null,
      properties: null,
      tenants: null,
      owners: null,
      suppliers: null,
      ownerContracts: null,
    };

    const listTypes: ListType[] = ['incidents', 'contracts', 'properties', 'tenants', 'owners', 'suppliers', 'ownerContracts'];
    
    listTypes.forEach((listType) => {
      const stored = localStorage.getItem(`${STORAGE_KEY_PREFIX}${listType}`);
      if (stored) {
        try {
          initialStates[listType] = JSON.parse(stored);
        } catch (e) {
          console.error(`Error parsing stored state for ${listType}:`, e);
        }
      }
    });

    return initialStates;
  });

  const getListState = useCallback((listType: ListType): ListNavigationState | null => {
    return states[listType];
  }, [states]);

  const setListState = useCallback((listType: ListType, state: ListNavigationState) => {
    setStates((prev) => {
      const updated = { ...prev, [listType]: state };
      // Persistir en localStorage
      localStorage.setItem(`${STORAGE_KEY_PREFIX}${listType}`, JSON.stringify(state));
      return updated;
    });
  }, []);

  const clearListState = useCallback((listType: ListType) => {
    setStates((prev) => {
      const updated = { ...prev, [listType]: null };
      localStorage.removeItem(`${STORAGE_KEY_PREFIX}${listType}`);
      return updated;
    });
  }, []);

  const actions = useMemo(() => ({
    getListState,
    setListState,
    clearListState,
  }), [getListState, setListState, clearListState]);

  return (
    <ListNavigationStateContext.Provider value={actions}>
      {children}
    </ListNavigationStateContext.Provider>
  );
};
